import { PageHero } from "@/components/shared/PageHero";
import { Button } from "@/components/ui/button";
import { Link } from "@/lib/router-compat";
import { Phone, Printer, AlertTriangle, ArrowRight } from "lucide-react";

type Section = {
  season: string;
  window: string;
  intro: string;
  items: { task: string; detail: string }[];
};

const SECTIONS: Section[] = [
  {
    season: "Fall",
    window: "September – November",
    intro:
      "The most important round of the year in Minnesota. Almost everything that goes wrong in January is decided in October.",
    items: [
      {
        task: "Clear gutters after the last leaves drop",
        detail:
          "Not at the first leaf fall — the last. A gutter that clogs in November freezes solid in December, and a gutter full of ice is the starting point for most ice dams.",
      },
      {
        task: "Check that downspouts discharge well clear of the foundation",
        detail:
          "Four to six feet out. Extensions get knocked loose by mowers all summer, and meltwater dumped at the foundation is how you end up with a wet basement in March.",
      },
      {
        task: "Look over the roof from the ground with binoculars",
        detail:
          "Missing, curled or cracked shingles, and anything lifting along the edges. Pay attention to the flashing where the roof meets the chimney, vent pipes and valleys — flashing fails long before shingles do.",
      },
      {
        task: "Check attic insulation depth and that soffit vents are clear",
        detail:
          "This is the ice dam item. A warm attic melts snow on the roof, the meltwater refreezes at the cold eaves, and the dam backs water up under the shingles. Insulation keeps attic heat down; soffit vents keep the roof deck cold. Blocked vents undo the insulation.",
      },
      {
        task: "Seal attic bypasses",
        detail:
          "Recessed lights, the chimney chase, bath fans, the attic hatch. These leak household heat straight into the attic and are the single most common cause of ice dams on an otherwise well-insulated house.",
      },
      {
        task: "Walk the siding and caulk what has opened up",
        detail:
          "Check around windows, doors, hose bibs, dryer vents and electrical penetrations. Caulk before the first hard freeze — most exterior sealants will not cure properly below about 40°F.",
      },
      {
        task: "Cut branches back from the roof",
        detail:
          "Anything overhanging. Ice loading brings limbs down onto roofs every winter, and branches that rub shingles strip the granules off them.",
      },
      {
        task: "Shut off and drain exterior spigots, disconnect hoses",
        detail:
          "A hose left connected traps water in the sill cock and splits the pipe inside the wall. You will not find out until spring.",
      },
      {
        task: "Clear the deck and check the fasteners",
        detail:
          "Sweep debris out of the gaps between boards so meltwater drains instead of sitting. Tighten anything loose before frost heave works on it.",
      },
    ],
  },
  {
    season: "Winter",
    window: "December – February",
    intro:
      "Almost all of this is done from the ground or from inside the house. Do not get on an icy roof — the thing you are trying to prevent costs less than the fall.",
    items: [
      {
        task: "Watch the eaves for ice dams",
        detail:
          "A row of icicles along the gutter line, or a visible ridge of ice at the roof edge, means meltwater is refreezing where it should be draining. A few icicles are normal; a solid band of ice is not.",
      },
      {
        task: "Rake snow off the bottom three or four feet of roof after heavy snowfall",
        detail:
          "A roof rake with a long handle, standing on the ground. Clearing the eave edge removes the material an ice dam forms from. Never use a ladder in these conditions.",
      },
      {
        task: "Check the attic after a hard cold snap",
        detail:
          "Frost on the nail points or the underside of the sheathing means warm moist air is getting up there. Left alone through a few thaw cycles, that wets the decking.",
      },
      {
        task: "Watch ceilings and the tops of exterior walls for stains",
        detail:
          "Brown rings or damp patches, especially in rooms along the outside walls. A stain that appears mid-winter is water coming in, not condensation, and it will not dry out on its own.",
      },
      {
        task: "Keep exhaust vents clear of drifted snow",
        detail:
          "Furnace and water heater intakes, the dryer vent, radon discharge. Drifts and roof-slide snow block these, and a blocked combustion intake is a carbon monoxide problem, not a maintenance one.",
      },
      {
        task: "Keep an eye on low-slope and porch roofs",
        detail:
          "Flat and low-pitch sections hold snow that a steep roof sheds. Wet spring snow is far heavier than it looks.",
      },
      {
        task: "Never chip at ice with a hammer, shovel or pick",
        detail:
          "It is the fastest way to put a hole through shingles and flashing, and the damage is not visible until the thaw. Steaming is the safe removal method and it is not a DIY job.",
      },
    ],
  },
  {
    season: "Spring",
    window: "March – May",
    intro:
      "Damage assessment. Winter has finished doing whatever it was going to do, and this is when you find out what it did.",
    items: [
      {
        task: "Look for granules in the gutters and at the downspout outlets",
        detail:
          "Shingle granules look like coarse black sand. A little is normal on a new roof. Piles of it on an older roof mean the shingles are giving up their protective layer.",
      },
      {
        task: "Check the soffits and eaves for ice dam damage",
        detail:
          "Stained or sagging soffit panels, peeling paint along the eaves, or damp insulation in the attic above exterior walls. This is where last winter shows up.",
      },
      {
        task: "Inspect gutters for ice damage",
        detail:
          "Pulled-away hangers, split seams, sections that have lost their pitch. The weight of ice bends gutters out of alignment and they stop draining where they should.",
      },
      {
        task: "Walk the siding for cracks, popped nails and loose panels",
        detail:
          "Look hardest wherever snow piled against the wall and anywhere the plow or shovel reached. Freeze-thaw works panels loose over a single season.",
      },
      {
        task: "Check caulk around windows and doors",
        detail:
          "Expansion and contraction opens gaps every winter. Now is the right weather to redo it — warm enough to cure, before the humidity arrives.",
      },
      {
        task: "Inspect the deck ledger board and its flashing",
        detail:
          "Where the deck attaches to the house. This is a safety item rather than a cosmetic one: ledger failures are the most common cause of deck collapse, and water getting behind unflashed ledgers is what rots them.",
      },
      {
        task: "Check grading around the foundation",
        detail:
          "Soil should fall away from the house. Ground settles over winter and a low spot against the wall collects every bit of snowmelt.",
      },
    ],
  },
  {
    season: "Summer",
    window: "June – August",
    intro:
      "Storm season. Most Minnesota roof claims come from a handful of afternoons, and what you do in the week afterward matters more than anything else on this list.",
    items: [
      {
        task: "After any hail, check the soft metal first",
        detail:
          "Look at gutters, downspouts, roof vents, flashing and the fins on the air conditioner. Soft metal dents before shingles visibly bruise, so it tells you whether the storm was strong enough to have hurt the roof.",
      },
      {
        task: "Look for bruising and granule loss on the shingles",
        detail:
          "Hail damage on asphalt often reads as dark circular spots where granules were knocked away, not as obvious holes. It is easy to miss from the ground and easy to confuse with ordinary wear.",
      },
      {
        task: "Photograph everything, with dates",
        detail:
          "Before anyone touches anything. Insurance claims turn on evidence, and a dated photo taken the day after the storm is worth more than an estimate written a month later.",
      },
      {
        task: "Check for wind damage along the ridges and edges",
        detail:
          "Lifted, creased or missing shingles, usually starting at the ridge and the rake edges. A shingle that got folded back and laid down again is still broken.",
      },
      {
        task: "Confirm the attic is venting",
        detail:
          "An attic that is far hotter than outdoor air on a July afternoon is not moving air. Heat ages shingles from underneath and quietly takes years off the roof.",
      },
      {
        task: "Re-stain or seal the deck",
        detail:
          "Dry stretch, warm boards. Water that stops beading is water soaking in, and a deck that goes into a Minnesota winter saturated does not come out of it the same.",
      },
      {
        task: "Cut back anything growing against the siding",
        detail:
          "Shrubs and vines hold moisture against the wall and give insects a bridge to it. Leave a hand's width of air behind the plants.",
      },
    ],
  },
];

const CALL_INSTEAD = [
  "Anything that requires standing on the roof — particularly between November and March",
  "Ice dam removal. Steaming is the only method that does not damage shingles",
  "A ceiling stain that appeared over winter, or damp insulation in the attic",
  "Suspected hail or wind damage, before you call the insurer",
  "Any deck where the ledger board looks soft, stained or poorly flashed",
];

const MaintenanceChecklist = () => {
  return (
    <main className="bg-background">
      <PageHero
        badge="Free homeowner's guide"
        title="Minnesota home maintenance checklist"
        subtitle="A season-by-season walkthrough of the roof, siding, gutter and deck jobs that actually matter on a Minnesota home — what to check, when to check it, and which ones are worth calling someone for."
      />

      <section className="container mx-auto px-4 py-10 md:py-14">
        <div className="max-w-3xl mx-auto">
          <div className="flex flex-wrap gap-3 mb-10 print:hidden">
            <Button
              variant="outline"
              onClick={() => window.print()}
              className="font-semibold"
            >
              <Printer className="w-4 h-4 mr-2" aria-hidden="true" />
              Print this checklist
            </Button>
            <a href="tel:+16122989865">
              <Button variant="outline" className="font-semibold">
                <Phone className="w-4 h-4 mr-2" aria-hidden="true" />
                (612) 298-9865
              </Button>
            </a>
          </div>

          <div className="bg-primary/5 border-l-4 border-primary rounded-r-lg p-5 mb-12">
            <h2 className="font-heading font-bold text-foreground mb-2">
              If you only do one thing
            </h2>
            <p className="text-muted-foreground">
              Keep the attic cold and the gutters empty going into winter. Ice
              dams cause more damage to Minnesota homes than any storm does, and
              they are almost entirely a product of household heat escaping into
              the attic and meltwater having nowhere to go. Both are fall jobs.
            </p>
          </div>

          <div className="space-y-14">
            {SECTIONS.map((section) => (
              <section
                key={section.season}
                aria-labelledby={`season-${section.season}`}
              >
                <div className="border-b-2 border-secondary pb-3 mb-6">
                  <h2
                    id={`season-${section.season}`}
                    className="text-2xl md:text-3xl font-heading font-bold text-foreground"
                  >
                    {section.season}
                  </h2>
                  <p className="text-sm font-semibold text-secondary uppercase tracking-wide mt-1">
                    {section.window}
                  </p>
                </div>
                <p className="text-muted-foreground mb-6">{section.intro}</p>

                <ul className="space-y-5">
                  {section.items.map((item) => (
                    <li key={item.task} className="flex gap-4">
                      <span
                        className="mt-1 w-5 h-5 shrink-0 rounded border-2 border-muted-foreground/40"
                        aria-hidden="true"
                      />
                      <div>
                        <p className="font-semibold text-foreground">
                          {item.task}
                        </p>
                        <p className="text-muted-foreground text-sm mt-1">
                          {item.detail}
                        </p>
                      </div>
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </div>

          <section
            aria-labelledby="call-instead"
            className="mt-14 bg-card border border-border rounded-2xl p-6 md:p-8"
          >
            <div className="flex items-center gap-3 mb-4">
              <AlertTriangle
                className="w-6 h-6 text-secondary shrink-0"
                aria-hidden="true"
              />
              <h2
                id="call-instead"
                className="text-xl font-heading font-bold text-foreground"
              >
                Leave these to somebody with the gear
              </h2>
            </div>
            <p className="text-muted-foreground mb-4">
              Not upselling — these are the jobs where the cost of getting it
              wrong is much higher than the cost of the job.
            </p>
            <ul className="space-y-2">
              {CALL_INSTEAD.map((line) => (
                <li key={line} className="flex gap-3 text-muted-foreground">
                  <span
                    className="text-secondary font-bold shrink-0"
                    aria-hidden="true"
                  >
                    &middot;
                  </span>
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          </section>

          <section className="mt-10 text-center bg-primary/5 rounded-2xl p-8 print:hidden">
            <h2 className="text-2xl font-heading font-bold text-foreground mb-3">
              Want a second pair of eyes on it?
            </h2>
            <p className="text-muted-foreground mb-6 max-w-xl mx-auto">
              We'll walk the roof, the gutters and the siding and tell you what
              we find — free, and with no obligation to do anything about it.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/schedule">
                <Button
                  size="lg"
                  className="bg-secondary hover:bg-secondary/90 text-secondary-foreground font-semibold px-8"
                >
                  Book a free inspection{" "}
                  <ArrowRight className="w-5 h-5 ml-2" aria-hidden="true" />
                </Button>
              </Link>
              <a href="tel:+16122989865">
                <Button
                  size="lg"
                  variant="outline"
                  className="border-primary text-primary hover:bg-primary hover:text-primary-foreground font-semibold px-8"
                >
                  <Phone className="w-5 h-5 mr-2" aria-hidden="true" /> (612)
                  298-9865
                </Button>
              </a>
            </div>
          </section>

          <p className="text-xs text-muted-foreground mt-10 text-center">
            Cedar Creek Construction &middot; Licensed Minnesota contractor
            BC645307 &middot; Serving the Minneapolis and Saint Paul north
            metro.
          </p>
        </div>
      </section>
    </main>
  );
};

export default MaintenanceChecklist;
